import React, { memo, useCallback, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { Collapsible } from '@atlassian/jira-platform-collapsible/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { NavigationProject } from '@atlassian/jira-router-resources-business-navigation/src/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import ProjectItem from '../../../common/ui/project-item/index.tsx';
import ActiveProject from '../active-project/index.tsx';
import messages from './messages.tsx';
import ViewAllProjects from './view-all-projects/index.tsx';

const PROJECT_LINK_TEST_ID = 'navigation-apps-sidebar-core.ui.multi-project-menu.project-link';

const getDefaultProjectUrl = (project: Pick<NavigationProject, 'key' | 'defaultView'>) =>
	project.defaultView
		? `/jira/core/projects/${project.key}/${project.defaultView}`
		: `/jira/core/projects/${project.key}`;

type Props = {
	sectionId: 'starred' | 'recent';
	projects: NavigationProject[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onFavoriteToggle: (project: NavigationProject) => any;
	currentProjectKey?: string;
	showViewAllProjects?: boolean;
	viewAllProjectsLabel?: string;
};

const ProjectGroup = ({
	sectionId,
	projects,
	onFavoriteToggle,
	currentProjectKey,
	showViewAllProjects = false,
	viewAllProjectsLabel,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const title =
		sectionId === 'starred' ? formatMessage(messages.favorites) : formatMessage(messages.recents);

	const [isExpanded, setIsExpanded] = useState(true);

	const onToggleExpand = useCallback(() => {
		setIsExpanded(!isExpanded);

		fireUIAnalytics(createAnalyticsEvent({}), 'section toggled', 'sidebar', {
			sectionId,
			collapse: isExpanded,
		});
	}, [createAnalyticsEvent, isExpanded, sectionId]);

	const chevronIcon = useMemo(
		() => (
			<IconWrapper isExpanded={isExpanded} visualRefresh={isVisualRefreshEnabled()}>
				<ChevronDownIcon
					label={formatMessage(isExpanded ? messages.collapseSection : messages.expandSection, {
						section: title,
					})}
					color={token('color.text.subtlest')}
					size="small"
				/>
			</IconWrapper>
		),
		[formatMessage, isExpanded, title],
	);

	return (
		<ProjectGroupWrapper
			data-testid={`navigation-apps-sidebar-core.ui.multi-project-menu.project-group.${sectionId}`}
		>
			<ProjectGroupTitle
				aria-expanded={isExpanded}
				aria-controls={`multi-project-menu-project-group-${sectionId}`}
				onClick={onToggleExpand}
				data-testid="navigation-apps-sidebar-core.ui.multi-project-menu.project-group.toggle"
			>
				<Button appearance="subtle" spacing="none" iconBefore={chevronIcon} />
				{title}
			</ProjectGroupTitle>

			<Collapsible
				id={`multi-project-menu-project-group-${sectionId}`}
				isExpanded={isExpanded}
				resetOverflow={false}
			>
				<ProjectsList>
					{projects.map((project) =>
						project.key === currentProjectKey ? (
							<li key={project.key}>
								<ActiveProject
									project={project}
									to={getDefaultProjectUrl(project)}
									onFavoriteToggle={() => onFavoriteToggle(project)}
								/>
							</li>
						) : (
							<li key={project.key}>
								<ProjectItem
									project={project}
									to={getDefaultProjectUrl(project)}
									onClick={() =>
										fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'jwmProjectLink', {
											projectId: project.id,
											starred: project.isFavorite,
											current: false,
										})
									}
									onFavoriteToggle={() => onFavoriteToggle(project)}
									prefetch="hover"
									testId={PROJECT_LINK_TEST_ID}
								/>
							</li>
						),
					)}

					{showViewAllProjects && (
						<li>
							<ViewAllProjects label={viewAllProjectsLabel} />
						</li>
					)}
				</ProjectsList>
			</Collapsible>
		</ProjectGroupWrapper>
	);
};

export default memo<Props>(ProjectGroup);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div<{ isExpanded: boolean; visualRefresh: boolean }>({
	transition: 'transform 0.2s',
	transitionProperty: 'transform',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	transform: ({ isExpanded }) => (isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	padding: ({ visualRefresh }) => (visualRefresh ? token('space.075') : null),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ProjectGroupWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginTop: 0,
	marginRight: token('space.negative.050'),
	marginBottom: 0,
	marginLeft: token('space.negative.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& + &': {
		marginTop: token('space.200'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ProjectGroupTitle = styled.span({
	color: token('color.text.subtlest'),
	display: 'flex',
	margin: 0,
	paddingTop: 0,
	paddingRight: token('space.050'),
	paddingBottom: 0,
	paddingLeft: token('space.050'),
	alignItems: 'center',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',

	font: token('font.heading.xxsmall'),
	gap: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ProjectsList = styled.ol({
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
	margin: 0,
	listStyle: 'none',
});
